import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    Typography,
    Grid,
    Button,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    useMediaQuery,
    Stack
} from '@mui/material';
import { Edit, Delete, MoreVert } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import AppSidebar from '../../../../components/navbar/DesktopAppSidebar';
import { Loading } from '../../../../components/loading/Loading';
import { useUserContext } from '../../../../context/user_context/UserContext';
import { useToast, ToastType } from '../../../../context/toast/ToastContext';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import ConfirmDeleteModal from '../../../../components/modals/ConfirmDeleteModal';
import usePermissions from '../../../../utils/permissions/UsePermissions';
import { Modules } from '../../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../../utils/permissions/PermissionTypes';
import { performanceReviewTemplateSnapshotListener, deletePerformanceReviewTemplate } from '../../../../repository/PerformanceReviewDAO';
import { EditPerformanceReviewTemplateModal } from './modals/EditPerformanceReviewTemplateModal';
import { handleMenuClick, handleMenuClose } from '../../../../components/utils/MenuHandlers';

function PerformanceReviewTemplateDetail() {
    const { templateId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width:600px)');
    const [template, setTemplate] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const { firebaseUser } = useUserContext();
    const { setLoadingData } = useLoadingContext();
    const { showToast } = useToast();
    const { hasPermission } = usePermissions();
    const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);

    useEffect(() => {
        if (firebaseUser && templateId) {
            const unsubscribe = performanceReviewTemplateSnapshotListener(
                firebaseUser.organization,
                templateId,
                setTemplate
            );
            return () => unsubscribe();
        }
    }, [firebaseUser, templateId]);

    const handleDeleteTemplate = async () => {
        try {
            setLoadingData(true);
            const result = await deletePerformanceReviewTemplate(firebaseUser.organization, template.id);
            if (result.success) {
                showToast(t(StringKeys.SUCCESSFULLY_DELETED_X, { x: template.name }), ToastType.SUCCESS);
                navigate('/performance-review-templates');
            } else {
                showToast(t(StringKeys.FAILED_TO_DELETE_X, { x: template.name }), ToastType.DANGER);
            }
        } catch (error) {
            console.error('Error deleting template:', error);
            showToast(t(StringKeys.FAILED_TO_DELETE_X, { x: template.name }), ToastType.DANGER);
        } finally {
            setLoadingData(false);
        }
    };

    if (!template) return <AppSidebar content={<Loading />} />;

    return (
        <AppSidebar
            content={
                <Stack gap={4}>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item xs>
                            <Typography variant={isMobile ? "h6" : "h5"}>{template.name}</Typography>
                        </Grid>
                        {!isMobile && hasPermission(Modules.EMPLOYEES, PermissionTypes.WRITE) && (
                            <Grid item>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="text"
                                        startIcon={<Edit />}
                                        onClick={() => setShowEditTemplateModal(true)}
                                    >
                                        {t(StringKeys.EDIT)}
                                    </Button>
                                    <Button
                                        variant="text"
                                        startIcon={<Delete />}
                                        onClick={() => setConfirmOpen(true)}
                                    >
                                        {t(StringKeys.DELETE)}
                                    </Button>
                                </Stack>
                            </Grid>
                        )}
                        {isMobile && hasPermission(Modules.EMPLOYEES, PermissionTypes.WRITE) && (
                            <Grid item>
                                <IconButton onClick={(event) => handleMenuClick(event, setAnchorEl)}>
                                    <MoreVert />
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={() => handleMenuClose(setAnchorEl)}
                                >
                                    <MenuItem onClick={() => {
                                        setShowEditTemplateModal(true);
                                        handleMenuClose(setAnchorEl);
                                    }}>
                                        <ListItemIcon>
                                            <Edit fontSize="small" />
                                        </ListItemIcon>
                                        {t(StringKeys.EDIT)}
                                    </MenuItem>
                                    <MenuItem onClick={() => {
                                        setConfirmOpen(true);
                                        handleMenuClose(setAnchorEl);
                                    }}>
                                        <ListItemIcon>
                                            <Delete fontSize="small" />
                                        </ListItemIcon>
                                        {t(StringKeys.DELETE)}
                                    </MenuItem>
                                </Menu>
                            </Grid>
                        )}
                    </Grid>

                    <Card variant="outlined">
                        <CardContent>
                            {template.questions.map((question, index) => (
                                <div key={question.id} style={{ marginBottom: '20px' }}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        {`${index + 1}. ${question.text}`}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {t(StringKeys.SECTION)}: {t(question.section.toUpperCase())}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {t(StringKeys.TYPE)}: {question.type === 'multipleChoice' ? t(StringKeys.MULTIPLE_CHOICE) : t(StringKeys.FREE_FORM)}
                                    </Typography>
                                </div>
                            ))}
                        </CardContent>
                    </Card>

                    <ConfirmDeleteModal
                        open={confirmOpen}
                        onClose={() => setConfirmOpen(false)}
                        onConfirm={handleDeleteTemplate}
                        item={template.name}
                    />

                    <EditPerformanceReviewTemplateModal
                        show={showEditTemplateModal}
                        onHide={() => setShowEditTemplateModal(false)}
                        template={template}
                    />
                </Stack>
            }
        />
    );
}

export default PerformanceReviewTemplateDetail;
